// Fine-grained anatomical muscle taxonomy for the Trainer / Balance tab body
// map (MUSCLE_BODY_MAP_PLAN.md, D-BM-3). Layered ON TOP of the existing coarse
// MuscleGroup taxonomy in gym.ts — `MUSCLE_GROUPS` and `computeMuscleLoad` are
// UNCHANGED (D-BM-4). Everything in this module is strictly additive: the
// existing Balance bars, bucket math, and `/gym/recommendations` behavior keep
// their exact current output. A future "optimization" that folds this module's
// math into computeMuscleLoad would silently change every existing chart —
// don't do that.

import { localCalendarDate } from "./datetime.js";
import type { MuscleGroup, GymExercisePayload } from "./gym.js";
import type { MuscleRecovery } from "./trainingLoad.js";

/** Which rendered view a muscle is visible in. */
export const BODY_VIEWS = ["front", "back", "side"] as const;
export type BodyView = (typeof BODY_VIEWS)[number];

/** Fine-grained anatomical muscle taxonomy (53 ids, D-BM-3). Every id maps to
 *  exactly one parent MuscleGroup via MUSCLE_META. */
export const ANATOMICAL_MUSCLES = [
  // chest
  "pec-clavicular", "pec-sternal", "serratus-anterior",
  // back
  "lat-dorsi", "traps-upper", "traps-mid", "traps-lower",
  "rhomboids", "erector-spinae", "teres-major", "rotator-cuff",
  "multifidus", "quadratus-lumborum",
  // shoulders
  "delt-anterior", "delt-lateral", "delt-posterior",
  // biceps
  "biceps-brachii", "brachialis",
  // triceps
  "triceps-long", "triceps-lateral",
  // forearms
  "forearm-flexors", "forearm-extensors",
  // core
  "rectus-abdominis", "obliques", "transverse-abdominis", "pelvic-floor",
  // glutes
  "glute-maximus", "glute-medius", "glute-minimus",
  // quads
  "rectus-femoris", "vastus-lateralis", "vastus-medialis", "vastus-intermedius",
  // hamstrings
  "biceps-femoris", "semitendinosus", "semimembranosus",
  // calves (the coarse group that also carries the shin and foot muscles)
  "gastrocnemius", "soleus", "tibialis-anterior",
  "peroneals", "tibialis-posterior", "plantar-intrinsics",
  // hip-abductors — abductors ONLY since the adductor split. "hip-adductors"
  // moved out into its own "adductors" group; FALLBACK_MUSCLES_BY_GROUP below
  // is what keeps events stored under the old grab-bag tag honest (D-3).
  "tfl",
  // neck
  "sternocleidomastoid", "scalenes", "deep-cervical-flexors",
  "levator-scapulae", "splenius-capitis",
  // hip-flexors
  "psoas-major", "iliacus", "sartorius",
  // adductors
  "hip-adductors", "gracilis",
] as const;
export type AnatomicalMuscle = (typeof ANATOMICAL_MUSCLES)[number];

/** Guard: true when `value` is a valid ANATOMICAL_MUSCLES member. */
export function isAnatomicalMuscle(value: string): value is AnatomicalMuscle {
  return (ANATOMICAL_MUSCLES as readonly string[]).includes(value);
}

export interface MuscleMeta {
  /** Anatomical name, e.g. "Rectus Femoris". */
  label: string;
  /** Plain-English name shown under the label, e.g. "Front Thigh". */
  common: string;
  /** One sentence: what the muscle does and which lifts train it. Shown in the
   *  body-map hover tooltip — training context, not clinical anatomy. */
  blurb: string;
  /** Parent coarse group — never "cardio" or "full-body" (D-BM-7). */
  group: MuscleGroup;
  /** Views in which this muscle has at least one region path. */
  views: BodyView[];
  /** True when the muscle is rendered as separate -l / -r paths (D-BM-8). */
  paired: boolean;
  /** True for a deep muscle with no distinct surface region; shares another
   *  muscle's region ids and is exempt from the validator's coverage rule. */
  deep?: boolean;
}

export const MUSCLE_META: Record<AnatomicalMuscle, MuscleMeta> = {
  "pec-clavicular": { label: "Pectoralis Major (Clavicular Head)", common: "Upper Chest", blurb: "Upper chest fibers that drive incline pressing and shoulder flexion. Trained by incline bench press and low-to-high cable flyes.", group: "chest", views: ["front", "side"], paired: true },
  "pec-sternal": { label: "Pectoralis Major (Sternal Head)", common: "Chest", blurb: "The bulk of the chest; drives horizontal pressing and pulls the arm across the body. Trained by flat bench press, push-ups, and chest flyes.", group: "chest", views: ["front", "side"], paired: true },
  "serratus-anterior": { label: "Serratus Anterior", common: "Side Ribs", blurb: "Wraps the upper ribs and rotates the shoulder blade upward; stabilizes the shoulder during pressing overhead. Trained by push-up plus variations and overhead presses.", group: "chest", views: ["front", "side"], paired: true },

  "lat-dorsi": { label: "Latissimus Dorsi", common: "Lats", blurb: "The broad back muscle that gives back 'width'; pulls the arm down and back. Trained by pull-ups, lat pulldowns, and rows.", group: "back", views: ["back", "side"], paired: true },
  "traps-upper": { label: "Trapezius (Upper)", common: "Upper Traps", blurb: "Elevates the shoulder blades and supports the neck; the muscle worked by a shrug. Trained by shrugs and heavy carries.", group: "back", views: ["front", "back", "side"], paired: true },
  "traps-mid": { label: "Trapezius (Middle)", common: "Mid Traps", blurb: "Pulls the shoulder blades back toward the spine; key for upright posture. Trained by rows and face pulls.", group: "back", views: ["back"], paired: true },
  "traps-lower": { label: "Trapezius (Lower)", common: "Lower Traps", blurb: "Depresses and stabilizes the shoulder blades; often underdeveloped relative to the upper traps. Trained by Y-raises and pulldowns done with the blades pulled down.", group: "back", views: ["back"], paired: true },
  "rhomboids": { label: "Rhomboids", common: "Between Shoulder Blades", blurb: "Squeezes the shoulder blades together between the spine; supports upright posture. Trained by rows and band pull-aparts.", group: "back", views: ["back"], paired: true },
  "erector-spinae": { label: "Erector Spinae", common: "Lower Back", blurb: "Runs the length of the spine and extends/stabilizes the lower back. Trained by deadlifts, back extensions, and good mornings.", group: "back", views: ["back", "side"], paired: true },
  "teres-major": { label: "Teres Major", common: "Upper Back / Armpit", blurb: "Assists the lats in pulling the arm down and inward; sits near the armpit. Trained alongside the lats by pull-ups and rows.", group: "back", views: ["back", "side"], paired: true },
  "rotator-cuff": { label: "Rotator Cuff", common: "Deep Shoulder", blurb: "Small deep muscles that stabilize the shoulder joint; matter for injury prevention more than size. Trained by band external/internal rotations and light overhead control work.", group: "back", views: ["back"], paired: true },
  "multifidus": { label: "Multifidus", common: "Deep Spinal Stabilizers", blurb: "The short deep muscles bridging one vertebra to the next; they stiffen the spine segment by segment rather than bending it. Trained by bird dogs, dead bugs, and any braced carry.", group: "back", views: ["back"], paired: true, deep: true },
  "quadratus-lumborum": { label: "Quadratus Lumborum", common: "Deep Side Lower Back", blurb: "Deep muscle between the last rib and the pelvis; hikes the hip and resists the trunk folding sideways. Trained by suitcase carries, side planks, and single-arm farmer's walks.", group: "back", views: ["back"], paired: true, deep: true },

  "delt-anterior": { label: "Anterior Deltoid", common: "Front Shoulder", blurb: "Front of the shoulder; raises the arm forward and assists pressing. Trained by overhead press and front raises.", group: "shoulders", views: ["front", "side"], paired: true },
  "delt-lateral": { label: "Lateral Deltoid", common: "Side Shoulder", blurb: "Side of the shoulder; gives shoulder width and lifts the arm out to the side. Trained by lateral raises.", group: "shoulders", views: ["front", "back", "side"], paired: true },
  "delt-posterior": { label: "Posterior Deltoid", common: "Rear Shoulder", blurb: "Rear of the shoulder; pulls the arm backward and supports posture — often neglected relative to the front delts. Trained by rear-delt flyes and face pulls.", group: "shoulders", views: ["back", "side"], paired: true },

  "biceps-brachii": { label: "Biceps Brachii", common: "Biceps", blurb: "Front of the upper arm; flexes the elbow and rotates the forearm. Trained by curls and chin-ups.", group: "biceps", views: ["front", "side"], paired: true },
  "brachialis": { label: "Brachialis", common: "Outer Upper Arm", blurb: "Sits beneath the biceps and drives raw elbow-flexion strength. Trained by hammer curls and reverse curls.", group: "biceps", views: ["front", "side"], paired: true },

  "triceps-long": { label: "Triceps (Long Head)", common: "Back of Upper Arm", blurb: "The largest of the three triceps heads; crosses the shoulder joint, so it's trained best in a stretched position. Trained by overhead extensions.", group: "triceps", views: ["back", "side"], paired: true },
  "triceps-lateral": { label: "Triceps (Lateral Head)", common: "Outer Triceps", blurb: "Outer head of the triceps; gives the arm its 'horseshoe' shape. Trained by pressdowns and close-grip presses.", group: "triceps", views: ["back", "side"], paired: true },

  "forearm-flexors": { label: "Forearm Flexors", common: "Inner Forearm", blurb: "Underside of the forearm; grips and curls the wrist. Loaded by grip work, curls, and any heavy pulling.", group: "forearms", views: ["front", "side"], paired: true },
  "forearm-extensors": { label: "Forearm Extensors", common: "Outer Forearm", blurb: "Top of the forearm; extends the wrist and balances the flexors. Trained by reverse curls and wrist extensions.", group: "forearms", views: ["front", "back", "side"], paired: true },

  "rectus-abdominis": { label: "Rectus Abdominis", common: "Abs", blurb: "The 'six-pack' muscle; flexes the spine in a crunching motion. Trained by crunches, leg raises, and cable crunches.", group: "core", views: ["front", "side"], paired: true },
  "obliques": { label: "Obliques", common: "Side Abs", blurb: "Sides of the core; rotate and side-bend the trunk. Trained by Russian twists and woodchoppers.", group: "core", views: ["front", "side"], paired: true },
  "transverse-abdominis": { label: "Transverse Abdominis", common: "Deep Core", blurb: "Deepest core layer; braces the spine like a corset rather than moving it. Trained by planks and dead bugs.", group: "core", views: ["front"], paired: false, deep: true },
  "pelvic-floor": { label: "Pelvic Floor", common: "Pelvic Floor", blurb: "The sling across the base of the pelvis that supports the organs and braces together with the deep core. Trained by Kegels and breath-coordinated bracing work.", group: "core", views: ["front"], paired: false, deep: true },

  "glute-maximus": { label: "Gluteus Maximus", common: "Glutes", blurb: "The largest glute muscle; drives hip extension and is the main engine behind squats and deadlifts. Isolated especially well by hip thrusts.", group: "glutes", views: ["back", "side"], paired: true },
  "glute-medius": { label: "Gluteus Medius", common: "Side Glutes", blurb: "Side of the hip; stabilizes the pelvis and abducts the leg. Trained by lateral band walks and side-lying leg raises.", group: "glutes", views: ["back", "side"], paired: true },
  "glute-minimus": { label: "Gluteus Minimus", common: "Deep Side Glute", blurb: "The smallest, deepest glute, tucked under the medius; abducts the hip and keeps the pelvis level in single-leg stance. Trained by clamshells, side-lying leg raises, and step-downs.", group: "glutes", views: ["back", "side"], paired: true, deep: true },

  "rectus-femoris": { label: "Rectus Femoris", common: "Front Thigh", blurb: "Front thigh muscle that crosses both the hip and knee, extending the knee and flexing the hip. Trained by squats and leg extensions.", group: "quads", views: ["front", "side"], paired: true },
  "vastus-lateralis": { label: "Vastus Lateralis", common: "Outer Thigh", blurb: "Outer sweep of the quad; extends the knee. Emphasized by squats and leg press with a wider stance.", group: "quads", views: ["front", "side"], paired: true },
  "vastus-medialis": { label: "Vastus Medialis", common: "Inner Thigh (Above Knee)", blurb: "Inner quad just above the knee; extends the knee and helps stabilize the kneecap. Emphasized by leg extensions done through full lockout.", group: "quads", views: ["front"], paired: true },
  "vastus-intermedius": { label: "Vastus Intermedius", common: "Deep Front Thigh", blurb: "The quad head hidden underneath the rectus femoris; extends the knee alongside the other three. Trained by squats, leg press, and leg extensions.", group: "quads", views: ["front"], paired: true, deep: true },

  "biceps-femoris": { label: "Biceps Femoris", common: "Outer Hamstring", blurb: "Outer hamstring; bends the knee and extends the hip. Trained by Romanian deadlifts and leg curls.", group: "hamstrings", views: ["back", "side"], paired: true },
  "semitendinosus": { label: "Semitendinosus", common: "Inner Hamstring", blurb: "Inner hamstring that works alongside the biceps femoris to bend the knee. Trained by leg curls, especially with a rotated stance.", group: "hamstrings", views: ["back"], paired: true },
  "semimembranosus": { label: "Semimembranosus", common: "Inner Hamstring (Deep)", blurb: "The broad inner hamstring lying beneath the semitendinosus; extends the hip and bends the knee. Trained by Romanian deadlifts, good mornings, and leg curls.", group: "hamstrings", views: ["back"], paired: true },

  "gastrocnemius": { label: "Gastrocnemius", common: "Calf", blurb: "The visible calf muscle; points the foot downward and is most active with a straight knee. Trained by standing calf raises.", group: "calves", views: ["front", "back", "side"], paired: true },
  "soleus": { label: "Soleus", common: "Lower Calf", blurb: "Deep calf muscle beneath the gastrocnemius; most active with a bent knee. Isolated by seated calf raises.", group: "calves", views: ["front", "back", "side"], paired: true },
  "tibialis-anterior": { label: "Tibialis Anterior", common: "Shin", blurb: "Front of the shin; lifts the foot and is a common site of overuse soreness in runners. Trained by toe raises and resisted dorsiflexion.", group: "calves", views: ["front", "side"], paired: true },
  "peroneals": { label: "Fibularis (Peroneal) Group", common: "Outer Lower Leg", blurb: "Outer lower-leg muscles that turn the sole outward; the ankle's main defence against rolling it. Trained by resisted ankle eversion and single-leg balance work.", group: "calves", views: ["front", "side"], paired: true },
  "tibialis-posterior": { label: "Tibialis Posterior", common: "Deep Inner Shin", blurb: "Deep muscle behind the shin that turns the sole inward and holds the arch up. Trained by resisted ankle inversion and heel raises kept off the outer edge of the foot.", group: "calves", views: ["front", "side"], paired: true, deep: true },
  "plantar-intrinsics": { label: "Plantar Intrinsic Muscles", common: "Arch of the Foot", blurb: "The small muscles inside the foot itself that hold the arch up and grip the ground. Trained by toe spreads, short-foot drills, and barefoot balance work.", group: "calves", views: ["front", "side"], paired: true },

  "tfl": { label: "Tensor Fasciae Latae", common: "Hip / Outer Thigh", blurb: "Front-outer hip muscle that assists hip flexion and abduction and tensions the IT band. Trained by standing band abductions.", group: "hip-abductors", views: ["front", "side"], paired: true },

  "sternocleidomastoid": { label: "Sternocleidomastoid", common: "Front Neck", blurb: "Front-neck strap that turns and flexes the head; commonly tight with forward-head posture. Trained by chin tucks and resisted neck flexion.", group: "neck", views: ["front", "side"], paired: true },
  "scalenes": { label: "Scalene Group", common: "Side Neck", blurb: "Side-neck muscles that bend the neck sideways and lift the upper ribs during hard breathing. Trained by resisted lateral neck flexion and loaded carries.", group: "neck", views: ["front", "side"], paired: true, deep: true },
  "deep-cervical-flexors": { label: "Deep Cervical Flexors", common: "Deep Front Neck", blurb: "Deep muscles in front of the cervical spine that hold the head stacked over the shoulders. Trained by chin tucks and supine head nods.", group: "neck", views: ["front"], paired: true, deep: true },
  "levator-scapulae": { label: "Levator Scapulae", common: "Side-Back of Neck", blurb: "Runs from the upper neck to the top corner of the shoulder blade, lifting it and side-bending the neck; the muscle behind most 'knot in the neck' complaints. Trained by shrugs and resisted neck side-bends, and stretched by the classic look-down-and-away stretch.", group: "neck", views: ["back", "side"], paired: true },
  "splenius-capitis": { label: "Splenius Capitis", common: "Back of Neck", blurb: "Back-of-neck strap that extends and rotates the head; the antagonist to the deep cervical flexors. Trained by resisted neck extension and neck-harness work.", group: "neck", views: ["back"], paired: true },

  "psoas-major": { label: "Psoas Major", common: "Deep Hip Flexor", blurb: "Deep hip flexor running from the lumbar spine to the inner thigh bone; drives knee lift and stabilizes the low back. Trained by hanging knee raises and standing band hip flexion.", group: "hip-flexors", views: ["front"], paired: true, deep: true },
  "iliacus": { label: "Iliacus", common: "Inner Hip", blurb: "Fans across the inside of the pelvis and joins the psoas to flex the hip. Trained by the same knee-raise and marching work that loads the psoas.", group: "hip-flexors", views: ["front"], paired: true, deep: true },
  "sartorius": { label: "Sartorius", common: "Front-Thigh Strap", blurb: "The long strap crossing the thigh from the point of the hip to the inner knee; flexes the hip and turns it out. Trained by cross-body knee raises and Cossack squats.", group: "hip-flexors", views: ["front", "side"], paired: true },

  "hip-adductors": { label: "Adductor Group (Magnus / Longus / Brevis)", common: "Inner Thigh", blurb: "Inner thigh muscles that pull the leg toward the midline and stabilize the hip during single-leg work. Trained by cable adduction and sumo-stance squats.", group: "adductors", views: ["front"], paired: true },
  "gracilis": { label: "Gracilis", common: "Inner Thigh (Long)", blurb: "The most superficial inner-thigh muscle, running all the way past the knee; adducts the hip and assists knee flexion. Trained by cable adduction and Copenhagen planks.", group: "adductors", views: ["front"], paired: true },
};

/** Inverse of MUSCLE_META.group. Every MUSCLE_GROUPS member EXCEPT "cardio"
 *  and "full-body" has at least one entry; those two map to []. */
export const MUSCLES_BY_GROUP: Record<MuscleGroup, AnatomicalMuscle[]> = (() => {
  const byGroup = {
    chest: [], back: [], shoulders: [], biceps: [], triceps: [], forearms: [],
    core: [], glutes: [], quads: [], hamstrings: [], calves: [], "hip-abductors": [],
    neck: [], "hip-flexors": [], adductors: [],
    cardio: [], "full-body": [],
  } as Record<MuscleGroup, AnatomicalMuscle[]>;
  for (const id of ANATOMICAL_MUSCLES) {
    byGroup[MUSCLE_META[id].group].push(id);
  }
  return byGroup;
})();

/** Coarse-group fan-out used ONLY by computeAnatomicalLoad's estimated
 *  fallback path (D-BM-6) — never by the coarse bucket math in gym.ts.
 *
 *  Identical to MUSCLES_BY_GROUP except "hip-abductors", which also fans out
 *  into "adductors": before the adductor split, every adductor machine
 *  denormalized the coarse tag "hip-abductors" onto its events. Shading only
 *  the TFL for those events would tell a user their adductors went untrained
 *  when they may not have — exactly what D-BM-6 exists to prevent. Load added
 *  through this path is `estimated`, so the map hatches it as a guess.
 *
 *  A future "optimization" that points computeAnatomicalLoad back at
 *  MUSCLES_BY_GROUP silently changes every pre-split chart. Do not. */
export const FALLBACK_MUSCLES_BY_GROUP: Record<MuscleGroup, AnatomicalMuscle[]> = {
  ...MUSCLES_BY_GROUP,
  "hip-abductors": [...MUSCLES_BY_GROUP["hip-abductors"], ...MUSCLES_BY_GROUP["adductors"]],
};

const PRIMARY_WEIGHT = 1.0;
const SECONDARY_WEIGHT = 0.5;
/** Per-muscle analogue of gym.ts's FULL_BODY_BUCKET_WEIGHT (D-BM-7). */
const FULL_BODY_MUSCLE_WEIGHT = 0.5;

export interface AnatomicalLoad {
  muscle: Record<AnatomicalMuscle, number>;
  musclePrimary: Record<AnatomicalMuscle, number>;
  muscleSecondary: Record<AnatomicalMuscle, number>;
  /** True when ALL of this muscle's load came from the coarse-group fallback
   *  (D-BM-6) rather than an explicit anatomical tag. Drives the hatch overlay. */
  estimated: Record<AnatomicalMuscle, boolean>;
  /** Weighted load from `cardio`-tagged events; has no body region (D-BM-7). */
  cardioLoad: number;
  /** ISO date of the most recent in-window event that loaded each muscle. */
  lastTrained: Partial<Record<AnatomicalMuscle, string>>;
  sessionCount: number;
}

function zeroRecord<T>(fill: () => T): Record<AnatomicalMuscle, T> {
  return Object.fromEntries(ANATOMICAL_MUSCLES.map((m) => [m, fill()])) as Record<AnatomicalMuscle, T>;
}

/**
 * @deprecated Use `computeTrainingLoad` from `trainingLoad.ts` (v2). This v1
 * function is left unchanged because `painCorrelation.ts` depends on its exact
 * output; removing it is a named Stage 4 task (D-TA-4).
 *
 * Computes anatomical-muscle-level training load from a set of gym_exercise
 * payloads, filtered to the trailing `days` window ending at `now`. Mirrors
 * computeMuscleLoad's shape exactly (D-BM-4) but is a separate, additive
 * computation over the fine-grained taxonomy.
 *
 * Per-payload rule:
 * 1. Filter to the window exactly as computeMuscleLoad does.
 * 2. If payload.primaryMuscleIds has entries, each valid id gets PRIMARY_WEIGHT
 *    (estimated stays false for it). Else, for each coarse group `g` in
 *    payload.primaryMuscles (skipping cardio/full-body), EVERY muscle in
 *    FALLBACK_MUSCLES_BY_GROUP[g] gets the full PRIMARY_WEIGHT and is marked
 *    estimated (D-BM-6 — never tell a user a muscle is untrained when it may
 *    have been trained; fallback load is NOT divided across the group). The
 *    fan-out goes through FALLBACK_MUSCLES_BY_GROUP rather than
 *    MUSCLES_BY_GROUP so an event stored under the pre-split "hip-abductors"
 *    grab-bag still shades the adductors (D-3).
 * 3. Same for secondary at SECONDARY_WEIGHT, through the same
 *    FALLBACK_MUSCLES_BY_GROUP fan-out.
 * 4. `cardio` in either coarse array adds to cardioLoad; no muscle touched.
 * 5. `full-body` in either coarse array gives every anatomical muscle
 *    FULL_BODY_MUSCLE_WEIGHT, marked estimated.
 * 6. Any muscle receiving load records lastTrained[m] = max(existing, performedAt).
 * 7. A muscle that receives any explicit (non-fallback) load has
 *    estimated[m] = false even if it also received fallback load from another event.
 */
export function computeAnatomicalLoad(
  payloads: GymExercisePayload[],
  days: number,
  now: Date = new Date(),
): AnatomicalLoad {
  const cutoff = now.getTime() - days * 24 * 60 * 60 * 1000;
  const muscle = zeroRecord(() => 0);
  const musclePrimary = zeroRecord(() => 0);
  const muscleSecondary = zeroRecord(() => 0);
  const estimated = zeroRecord(() => false);
  const explicitlyLoaded = zeroRecord(() => false);
  const lastTrained: Partial<Record<AnatomicalMuscle, string>> = {};
  let cardioLoad = 0;
  let sessionCount = 0;

  const touch = (m: AnatomicalMuscle, weight: number, isEstimated: boolean, performedAt: string) => {
    muscle[m] += weight;
    if (isEstimated) {
      if (!explicitlyLoaded[m]) estimated[m] = true;
    } else {
      explicitlyLoaded[m] = true;
      estimated[m] = false;
    }
    const prior = lastTrained[m];
    if (!prior || new Date(performedAt).getTime() > new Date(prior).getTime()) {
      lastTrained[m] = performedAt;
    }
  };

  for (const payload of payloads) {
    const performedAtMs = new Date(payload.performedAt).getTime();
    if (Number.isNaN(performedAtMs) || performedAtMs < cutoff || performedAtMs > now.getTime()) continue;
    sessionCount++;

    const applyIds = (
      ids: AnatomicalMuscle[] | undefined,
      coarseGroups: MuscleGroup[],
      weight: number,
      primaryBucket: Record<AnatomicalMuscle, number> | null,
    ) => {
      if (ids && ids.length > 0) {
        for (const m of ids) {
          if (!isAnatomicalMuscle(m)) continue;
          touch(m, weight, false, payload.performedAt);
          if (primaryBucket) primaryBucket[m] += weight;
        }
        return;
      }
      for (const g of coarseGroups) {
        if (g === "cardio") {
          cardioLoad += weight;
          continue;
        }
        if (g === "full-body") {
          for (const m of ANATOMICAL_MUSCLES) {
            touch(m, FULL_BODY_MUSCLE_WEIGHT, true, payload.performedAt);
            if (primaryBucket) primaryBucket[m] += FULL_BODY_MUSCLE_WEIGHT;
          }
          continue;
        }
        for (const m of FALLBACK_MUSCLES_BY_GROUP[g] ?? []) {
          touch(m, weight, true, payload.performedAt);
          if (primaryBucket) primaryBucket[m] += weight;
        }
      }
    };

    applyIds(payload.primaryMuscleIds, payload.primaryMuscles, PRIMARY_WEIGHT, musclePrimary);
    applyIds(payload.secondaryMuscleIds, payload.secondaryMuscles, SECONDARY_WEIGHT, muscleSecondary);
  }

  return { muscle, musclePrimary, muscleSecondary, estimated, cardioLoad, lastTrained, sessionCount };
}

// ── Lateral (left / right) load ────────────────────────────────────────────
// STRICTLY ADDITIVE, exactly like computeAnatomicalLoad one layer up (D-LAT-4).
// This is a NEW, SEPARATE computation — it is NOT wired into computeMuscleLoad,
// computeAnatomicalLoad, computeAntagonistRatios, or BALANCE_BUCKETS, and folding
// `side` into any of them would silently change every existing chart. Don't.
// The per-payload weighting below is copied VERBATIM from computeAnatomicalLoad —
// same window filter, same PRIMARY_WEIGHT / SECONDARY_WEIGHT, same
// FALLBACK_MUSCLES_BY_GROUP fan-out, same FULL_BODY_MUSCLE_WEIGHT, cardio
// contributes nothing — the ONLY addition is routing each computed weight to the
// subject's left / right / both copies of the muscle per D-LAT-3. A history of
// purely bilateral training therefore reads as an exactly balanced 1.0, which is
// correct; the UI is what checks `sidedSessionCount` and shows an empty state
// instead of "balanced" (D-LAT-5).

export interface LateralLoad {
  /** Load on the subject's LEFT-side copy of each muscle. */
  left: Record<AnatomicalMuscle, number>;
  /** Load on the subject's RIGHT-side copy of each muscle. */
  right: Record<AnatomicalMuscle, number>;
  /** In-window payloads carrying an explicit "left" or "right". Zero means every
   *  ratio is trivially 1.0 and the UI must show an empty state, not "balanced" (D-LAT-5). */
  sidedSessionCount: number;
  sessionCount: number;
}

/**
 * @deprecated Use `computeTrainingLoad` from `trainingLoad.ts` (v2). This v1
 * function is left unchanged because `painCorrelation.ts` depends on
 * `computeAnatomicalLoad`; laterality in v1 has no other remaining consumer.
 * Removing v1 is a named Stage 4 task (D-TA-4).
 */
export function computeLateralLoad(
  payloads: GymExercisePayload[],
  days: number,
  now: Date = new Date(),
): LateralLoad {
  const cutoff = now.getTime() - days * 24 * 60 * 60 * 1000;
  const left = zeroRecord(() => 0);
  const right = zeroRecord(() => 0);
  let sessionCount = 0;
  let sidedSessionCount = 0;

  for (const payload of payloads) {
    const performedAtMs = new Date(payload.performedAt).getTime();
    if (Number.isNaN(performedAtMs) || performedAtMs < cutoff || performedAtMs > now.getTime()) continue;
    sessionCount++;

    const side = payload.side ?? "both";
    if (side === "left" || side === "right") sidedSessionCount++;

    // D-LAT-3: side absent or "both" -> full weight to BOTH sides. "left"/"right"
    // -> that side only, EXCEPT midline/unpaired muscles (paired === false) which
    // always get both regardless of `side`.
    const route = (m: AnatomicalMuscle, weight: number) => {
      const paired = MUSCLE_META[m].paired;
      if (side !== "right" || !paired) left[m] += weight;
      if (side !== "left" || !paired) right[m] += weight;
    };

    const applyIds = (
      ids: AnatomicalMuscle[] | undefined,
      coarseGroups: MuscleGroup[],
      weight: number,
    ) => {
      if (ids && ids.length > 0) {
        for (const m of ids) {
          if (!isAnatomicalMuscle(m)) continue;
          route(m, weight);
        }
        return;
      }
      for (const g of coarseGroups) {
        if (g === "cardio") continue;
        if (g === "full-body") {
          for (const m of ANATOMICAL_MUSCLES) route(m, FULL_BODY_MUSCLE_WEIGHT);
          continue;
        }
        for (const m of FALLBACK_MUSCLES_BY_GROUP[g] ?? []) route(m, weight);
      }
    };

    applyIds(payload.primaryMuscleIds, payload.primaryMuscles, PRIMARY_WEIGHT);
    applyIds(payload.secondaryMuscleIds, payload.secondaryMuscles, SECONDARY_WEIGHT);
  }

  return { left, right, sidedSessionCount, sessionCount };
}

export type HeatScaleMode = "target" | "relative";
/** Weighted sessions per muscle per week that count as "fully trained" (D-BM-5).
 *  2.0 is the common training-frequency guideline (2x/week per muscle group).
 *  Per-patient override lives on Patient.weeklyMuscleTargetSessions (D-TGT-1). */
export const WEEKLY_TARGET_SESSIONS = 2.0;
/** Server-side clamp bounds for Patient.weeklyMuscleTargetSessions (D-TGT-1). */
export const MIN_WEEKLY_TARGET_SESSIONS = 0.5;
export const MAX_WEEKLY_TARGET_SESSIONS = 7;
/** Preset chips for the Balance tab's target control (D-TGT-2); a custom number
 *  input covers anything off-preset. */
export const WEEKLY_TARGET_PRESETS: { label: string; value: number; hint: string }[] = [
  { label: "Maintain", value: 1, hint: "About one session per muscle group per week - enough to keep the muscle and strength you already have. Good for a maintenance phase, a busy stretch, or training around an injury." },
  { label: "Standard", value: 2, hint: "Two sessions per muscle group per week - the common general-fitness and strength guideline, and a sensible default for most people." },
  { label: "Hypertrophy", value: 3, hint: "Three or more sessions per muscle group per week - a higher frequency aimed at maximising muscle growth. Needs more training time and recovery capacity." },
];
/** Working sets per muscle per week that count as "at target" (D-TA-5). 10 is
 *  the ACSM figure quoted in the trainer-accuracy audit — a starting default,
 *  not a validated prescription. Per-patient override: Patient.weeklySetTarget. */
export const WEEKLY_SET_TARGET = 10;
export const MIN_WEEKLY_SET_TARGET = 1;
export const MAX_WEEKLY_SET_TARGET = 60;

/** Target-mode heat keeps darkening past target, up to this multiple of the
 *  weekly target (D-HR-1). Relative mode stays 0..1. */
export const HEAT_OVER_TARGET_MAX = 2;

/** Returns heat (0..HEAT_OVER_TARGET_MAX in target mode, 1 = at target; 0..1 in
 *  relative mode), or null for "never trained in this window" (render cold/blue).
 *  `targetSessions` is optional and defaults to WEEKLY_TARGET_SESSIONS so no
 *  existing call site breaks and `load / undefined` (NaN) can never happen (hazard #6). */
export function muscleHeatT(
  load: number,
  opts: { mode: HeatScaleMode; windowDays: number; maxLoad: number; targetSessions?: number },
): number | null {
  if (load <= 0) return null;
  if (opts.mode === "target") {
    const target = (opts.targetSessions ?? WEEKLY_TARGET_SESSIONS) * (opts.windowDays / 7);
    return Math.min(HEAT_OVER_TARGET_MAX, load / target);
  }
  return opts.maxLoad > 0 ? Math.min(1, load / opts.maxLoad) : 0;
}

/** User-facing heat-scale labels (F15). Labelling only — does not change
 *  muscleHeatT. Neither caption may mention recovery, fatigue, or over-training. */
export function heatLegend(opts: {
  mode: HeatScaleMode;
  windowDays: number;
  targetSessions: number;
  maxLoad: number;
  measureLabel: string;   // "training days" | "working sets"
}): { rampEndLabel: string; caption: string; overTargetLabel?: string } {
  const label = `the last ${opts.windowDays} days`;
  const noPhysiology =
    " Colour does not measure recovery, fatigue, or over-training.";
  if (opts.mode === "target") {
    return {
      rampEndLabel: "at target",
      overTargetLabel: "2× target",
      caption:
        `Colour shows your share of ${opts.targetSessions}x/week (${opts.measureLabel}) over ${label}. Past the target it keeps darkening, up to twice the target; beyond that it looks the same.` +
        noPhysiology,
    };
  }
  return {
    rampEndLabel: "your busiest muscle",
    caption:
      `Colour is relative to your own busiest muscle in this window (${opts.maxLoad.toFixed(1)}). Full red is your maximum, not a target.` +
      noPhysiology,
  };
}

/** Display-only per-payload hit for one muscle (F10). Mirrors
 *  computeAnatomicalLoad's applyIds without weights. Trainer UI hatch/provenance
 *  reads TrainingLoad.tier / mixedAttribution instead. */
export type PayloadMuscleAttribution = "explicit" | "inferred" | "uncertain";

function payloadLevelHit(
  ids: AnatomicalMuscle[] | undefined,
  coarse: MuscleGroup[],
  muscle: AnatomicalMuscle,
): PayloadMuscleAttribution | null {
  if (ids && ids.length > 0) {
    return ids.includes(muscle) ? "explicit" : null;
  }
  for (const g of coarse) {
    if (g === "cardio") continue;
    if (g === "full-body") return "uncertain";
    if ((FALLBACK_MUSCLES_BY_GROUP[g] ?? []).includes(muscle)) return "inferred";
  }
  return null;
}

export interface MuscleAttributionCounts {
  explicit: number;
  inferred: number;
  uncertain: number;
}

/** Counts in-window logs that touched `muscle`, split by how they tagged it.
 *  Trainer UI hatch/provenance reads TrainingLoad.tier / mixedAttribution. */
export function muscleAttributionCounts(
  payloads: GymExercisePayload[],
  muscle: AnatomicalMuscle,
  inWindow: (iso: string) => boolean,
): MuscleAttributionCounts {
  let explicit = 0;
  let inferred = 0;
  let uncertain = 0;
  for (const payload of payloads) {
    if (!inWindow(payload.performedAt)) continue;
    const kinds = new Set<PayloadMuscleAttribution>();
    const pri = payloadLevelHit(payload.primaryMuscleIds, payload.primaryMuscles, muscle);
    const sec = payloadLevelHit(payload.secondaryMuscleIds, payload.secondaryMuscles, muscle);
    if (pri) kinds.add(pri);
    if (sec) kinds.add(sec);
    if (kinds.has("explicit")) explicit += 1;
    if (kinds.has("inferred")) inferred += 1;
    if (kinds.has("uncertain")) uncertain += 1;
  }
  return { explicit, inferred, uncertain };
}

/** Hatch state for the body map (v1 display derivation). Trainer UI uses
 *  hatchKindFromMeasures on TrainingLoad instead. */
export type MuscleHatchKind = "none" | "explicit" | "inferred" | "mixed";

export function muscleHatchKind(counts: MuscleAttributionCounts): MuscleHatchKind {
  if (counts.explicit > 0 && counts.inferred > 0) return "mixed";
  if (counts.explicit > 0) return "explicit";
  if (counts.inferred > 0 || counts.uncertain > 0) return "inferred";
  return "none";
}

// ── Balance-driven exercise recommendations (Log tab) ─────────────────────
// Deterministic, client-side, no Bedrock (D-5 / D-RR-10). Ranks candidate
// exercises by how much below-weekly-target (or due-again, D-DUE-1) anatomical
// muscle they cover, then
// hides a candidate when any primary is still recovering (D-RR-9 supersedes
// D-TA-10 for this strip only). Purely additive: does NOT touch
// computeBalanceCallouts or the AI /gym/recommendations endpoint.

/** Trailing-window length the Log tab's Recommended section always uses (D-6). */
export const RECOMMENDATION_WINDOW_DAYS = 7;
/** Max owned grid exercises shown in the Recommended section. */
export const MAX_BALANCE_RECOMMENDATIONS = 6;
/** Max catalog cards in Recommended: inventory-gap ideas first, then a daily
 *  variety rotation (D-IF-8). */
export const MAX_CATALOG_FALLBACKS = 2;
/** A candidate must accumulate at least this weighted deficit score to be shown
 *  at all — keeps a muscle that's a rounding error below target from generating
 *  noise. Tuned to half of PRIMARY_WEIGHT. Applied to undamped coverage so the
 *  gate still decides "is this below-target enough to count" (D-RR-9); recovering
 *  primaries are a separate eligibility gate, and recovery-day keeps the section
 *  from going silently empty. */
export const MIN_RECOMMENDATION_SCORE = 0.5;
/** Floor on a recovering *secondary*'s ranking weight on an eligible session
 *  candidate. Heuristic scheduling default, not a physiological measurement
 *  (D-RR-9). No longer exists to keep Recommended non-empty — recovery-day does. */
export const RECOVERY_MIN_FACTOR = 0.15;
/** How many of the worst-deficit muscles the catalog fallback is allowed to
 *  fill in for. */
export const CATALOG_FALLBACK_DEFICIT_MUSCLES = 3;
/** Push/pull boost among eligible session recs (D-RR-7). */
export const ANTAGONIST_BOOST = 1.15;
/** Blocked resistance may appear on a recovery day only at/above this recovered fraction. */
export const RECOVERY_LIGHT_MIN_FRACTION = 0.5;
/** Cap on recovery-day tiles (mobility first, then light resistance). */
export const MAX_RECOVERY_DAY_RECS = 4;
/** Excluded from recommendation deficits unless the patient opts in (D-IF-3).
 *  Still rendered and coloured on the map. Not a clinical claim. */
export const SPECIALIST_MUSCLES: ReadonlySet<AnatomicalMuscle> = new Set<AnatomicalMuscle>([
  "sternocleidomastoid", "scalenes", "deep-cervical-flexors", "levator-scapulae", "splenius-capitis",
  "pelvic-floor", "plantar-intrinsics", "tibialis-posterior", "peroneals",
  "transverse-abdominis", "multifidus", "quadratus-lumborum", "rotator-cuff",
]);
export function isSpecialistMuscle(m: AnatomicalMuscle): boolean {
  return SPECIALIST_MUSCLES.has(m);
}
/** Large prime movers first when deficits tie (D-IF-4). Not a clinical ranking. */
export const DEFICIT_GROUP_PRIORITY: MuscleGroup[] = [
  "quads", "hamstrings", "glutes", "back", "chest", "shoulders", "core", "calves",
  "adductors", "hip-abductors", "biceps", "triceps", "hip-flexors", "forearms", "neck",
];
/** Share of a pick's primaries an owned alternative must also hit (D-IF-2). */
export const ALTERNATIVE_MIN_OVERLAP = 0.5;
export const MAX_ALTERNATIVES = 3;
/** Rotation pool for variety catalog entries (D-IF-8). */
export const VARIETY_POOL_SIZE = 4;
/** Ranking need for a muscle that met its weekly target but is rested and past
 *  its target spacing (D-DUE-1). Equal to MIN_RECOMMENDATION_SCORE so a due
 *  primary alone qualifies, and half a one-session weekly gap so true gaps rank
 *  first. Scheduling heuristic, not a physiological measurement. */
export const DUE_AGAIN_NEED = 0.5;

/** Hours between sessions that a weekly target implies: 84h at 2x/week, a full
 *  week at 1x/week (D-DUE-1). */
export function dueAgainSpacingHours(targetSessions: number): number {
  return targetSessions > 0 ? (7 * 24) / targetSessions : Number.POSITIVE_INFINITY;
}

type SessionBucket = "push" | "pull" | "legs" | "core";

/**
 * Group → session bucket for antagonist boost (D-RR-7). Must stay aligned with
 * gym.ts `BALANCE_BUCKETS` for push/pull/legs/core. neck / cardio / full-body
 * have no pair. Do not value-import BALANCE_BUCKETS — gym.ts already
 * value-imports ANATOMICAL_MUSCLES from this file.
 */
const GROUP_BUCKET: Record<MuscleGroup, SessionBucket | undefined> = {
  chest: "push",
  shoulders: "push",
  triceps: "push",
  back: "pull",
  biceps: "pull",
  forearms: "pull",
  quads: "legs",
  hamstrings: "legs",
  glutes: "legs",
  calves: "legs",
  "hip-abductors": "legs",
  adductors: "legs",
  "hip-flexors": "legs",
  core: "core",
  neck: undefined,
  cardio: undefined,
  "full-body": undefined,
};

const ANTAGONIST: Partial<Record<SessionBucket, SessionBucket>> = {
  push: "pull",
  pull: "push",
};

/** Ranking weight for a recovering secondary that is not same-day. Ready /
 *  no-data / absent / same-day recovering → 1. Recovering (not same-day) is
 *  interpolated from RECOVERY_MIN_FACTOR up to 1. */
function recoveryFactor(r?: MuscleRecovery, sameDay = false): number {
  if (!r || r.state !== "recovering" || sameDay) return 1;
  return RECOVERY_MIN_FACTOR + (1 - RECOVERY_MIN_FACTOR) * r.fractionRecovered;
}

function collectAnatomical(
  ids: AnatomicalMuscle[] | undefined,
  groups: MuscleGroup[],
): AnatomicalMuscle[] {
  const out: AnatomicalMuscle[] = [];
  const add = (m: AnatomicalMuscle) => {
    if (!out.includes(m)) out.push(m);
  };
  if (ids && ids.length > 0) {
    for (const m of ids) if (isAnatomicalMuscle(m)) add(m);
    return out;
  }
  for (const g of groups) {
    if (g === "cardio" || g === "full-body") continue;
    for (const m of FALLBACK_MUSCLES_BY_GROUP[g] ?? []) add(m);
  }
  return out;
}

function primaryMusclesOf(c: RecommendationCandidate): AnatomicalMuscle[] {
  return collectAnatomical(c.primaryMuscleIds, c.primaryMuscles);
}

function isHardPurpose(c: RecommendationCandidate): boolean {
  const p = c.purpose;
  return p === undefined || p === "resistance" || p === "mixed" || p === "unknown";
}

function isRecoveryPurpose(c: RecommendationCandidate): boolean {
  const p = c.purpose;
  return p === "aerobic" || p === "mobility" || p === "motor-control";
}

function isSameDayRecovering(
  r: MuscleRecovery,
  timezone?: string,
  asOfIso?: string,
): boolean {
  if (r.state !== "recovering") return false;
  if (!timezone || !asOfIso || !r.lastResistanceAt) return false;
  return localCalendarDate(r.lastResistanceAt, timezone) === localCalendarDate(asOfIso, timezone);
}

function isHardEligible(
  c: RecommendationCandidate,
  recovery: Record<AnatomicalMuscle, MuscleRecovery> | undefined,
  timezone?: string,
  asOfIso?: string,
): boolean {
  if (!isHardPurpose(c)) return false;
  const primaries = primaryMusclesOf(c);
  if (primaries.length === 0) return false;
  return primaries.every((m) => {
    const r = recovery?.[m];
    if (!r || r.state !== "recovering") return true;
    return isSameDayRecovering(r, timezone, asOfIso);
  });
}

/** Hours until every recovering, not-same-day primary is ready; 0 when none blocks. */
export function primaryRestHoursRemaining(
  primaries: AnatomicalMuscle[],
  recovery: Record<AnatomicalMuscle, MuscleRecovery> | undefined,
  timezone?: string,
  asOfIso?: string,
): number {
  if (!recovery) return 0;
  let max = 0;
  for (const m of primaries) {
    const r = recovery[m];
    if (!r || r.state !== "recovering" || isSameDayRecovering(r, timezone, asOfIso)) continue;
    const hours = Math.ceil(Math.max(0, r.recoveryHours - (r.hoursSince ?? 0)));
    if (hours > max) max = hours;
  }
  return max;
}

/** Deterministic unsigned 32-bit FNV-1a. Variety rotation indexes with this (D-IF-8). */
function hash32(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function majorityBucket(muscles: AnatomicalMuscle[]): SessionBucket | undefined {
  const counts: Record<SessionBucket, number> = { push: 0, pull: 0, legs: 0, core: 0 };
  for (const m of muscles) {
    const bucket = GROUP_BUCKET[MUSCLE_META[m].group];
    if (bucket) counts[bucket]++;
  }
  let winner: SessionBucket | undefined;
  let max = 0;
  let tied = false;
  for (const b of ["push", "pull", "legs", "core"] as const) {
    if (counts[b] > max) {
      max = counts[b];
      winner = b;
      tied = false;
    } else if (counts[b] === max && max > 0) {
      tied = true;
    }
  }
  if (max === 0 || tied) return undefined;
  return winner;
}

function dominantBlockedBucket(
  recovery: Record<AnatomicalMuscle, MuscleRecovery> | undefined,
  timezone?: string,
  asOfIso?: string,
): SessionBucket | undefined {
  if (!recovery) return undefined;
  const blocked: AnatomicalMuscle[] = [];
  for (const m of ANATOMICAL_MUSCLES) {
    const r = recovery[m];
    if (!r || r.state !== "recovering") continue;
    if (isSameDayRecovering(r, timezone, asOfIso)) continue;
    blocked.push(m);
  }
  return majorityBucket(blocked);
}

function hasBlockedMuscle(
  recovery: Record<AnatomicalMuscle, MuscleRecovery>,
  timezone?: string,
  asOfIso?: string,
): boolean {
  return ANATOMICAL_MUSCLES.some((m) => {
    const r = recovery[m];
    return r?.state === "recovering" && !isSameDayRecovering(r, timezone, asOfIso);
  });
}

function candidateBucket(c: RecommendationCandidate): SessionBucket | undefined {
  return majorityBucket(primaryMusclesOf(c));
}

function byNameAsc(a: { name: string }, b: { name: string }): number {
  return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
}

/** Minimal structural shape a UserGymExercise, GymExerciseView, or
 *  GymCatalogEntry all already satisfy — keeps this module leaf-level. */
export interface RecommendationCandidate {
  id: string;
  source: "own" | "catalog";
  name: string;
  primaryMuscles: MuscleGroup[];
  secondaryMuscles: MuscleGroup[];
  primaryMuscleIds?: AnatomicalMuscle[];
  secondaryMuscleIds?: AnatomicalMuscle[];
  /** When omitted, treated as resistance (D-RR-13). */
  purpose?: "resistance" | "aerobic" | "mobility" | "motor-control" | "mixed" | "unknown";
  /** Own candidates: the catalog entry they came from, when one exists. */
  catalogId?: string;
}

export interface ScoredRecommendation {
  id: string;
  source: "own" | "catalog";
  name: string;
  /** Session recs: weighted below-target coverage, secondary-damped. Recovery-
   *  mobility is 0; recovery-light is min primary fractionRecovered. */
  score: number;
  /** Below-target or due-again anatomical muscles it touches, worst-deficit first. */
  coveredMuscles: AnatomicalMuscle[];
  /** Subset of coveredMuscles that are at target but due again (D-DUE-1).
   *  Always [] for catalog and recovery-day items. */
  dueMuscles: AnatomicalMuscle[];
  /** Covered muscles whose recovery state is `"recovering"`. Empty when no
   *  recovery map is supplied or none of the covered muscles are resting. */
  restingMuscles: AnatomicalMuscle[];
  kind: "session" | "recovery-mobility" | "recovery-light";
  /** True when eligibility passed only because of D-RR-4. */
  sameDay: boolean;
  /** Own session picks only; [] elsewhere. */
  alternatives: string[];
  catalogReason?: "inventory-gap" | "variety";
}

export type OwnNotShownReason = "no-muscle-tags" | "no-gap" | "recovering" | "covered-by-pick";

export interface OwnNotShown {
  id: string;
  name: string;
  reason: OwnNotShownReason;
  readyInHours?: number;
}

export interface BalanceRecommendations {
  /** Below-target anatomical muscles in the window, worst deficit first. */
  deficits: { muscle: AnatomicalMuscle; deficit: number }[];
  /** Muscles at (or within DUE_AGAIN_NEED of) target that are rested and were
   *  last trained at least one target spacing ago (D-DUE-1). Oldest first. */
  dueMuscles: AnatomicalMuscle[];
  /** Owned grid exercises to prioritise (capped to MAX_BALANCE_RECOMMENDATIONS). */
  own: ScoredRecommendation[];
  /** Catalog fill-ins: inventory gaps first, then variety (capped to
   *  MAX_CATALOG_FALLBACKS). */
  catalog: ScoredRecommendation[];
  recoveryDay: boolean;
  /** Below-target anatomical muscles whose primaries are recovering and not same-day. */
  deferredMuscles: AnatomicalMuscle[];
  /** Hard-purpose owned exercises that were not picked, with why. */
  ownNotShown: OwnNotShown[];
}

export function computeBalanceRecommendations(opts: {
  load: { muscle: Record<AnatomicalMuscle, number> };
  candidates: RecommendationCandidate[];
  targetSessions: number;
  windowDays: number;
  /** When absent, secondary ranking is undamped (D-RR-9). */
  recovery?: Record<AnatomicalMuscle, MuscleRecovery>;
  timezone?: string;
  asOfIso?: string;
  /** Default false. Specialist muscles stay out of deficits unless set (D-IF-3). */
  includeSpecialistMuscles?: boolean;
  /** Tie-break only (D-IF-4). Omitted skips the last-trained step. */
  lastTrainedAt?: Partial<Record<AnatomicalMuscle, string>>;
}): BalanceRecommendations {
  const {
    load, candidates, targetSessions, windowDays, recovery, timezone, asOfIso, lastTrainedAt,
  } = opts;
  const includeSpecialistMuscles = opts.includeSpecialistMuscles === true;
  const target = targetSessions * (windowDays / 7);
  const deficitScale = target > 0 ? 1 / target : 0;

  const deficitBy = {} as Record<AnatomicalMuscle, number>;
  for (const m of ANATOMICAL_MUSCLES) {
    if (!includeSpecialistMuscles && isSpecialistMuscle(m)) deficitBy[m] = 0;
    else deficitBy[m] = Math.max(0, target - (load.muscle[m] ?? 0));
  }
  const worstFirst = (a: AnatomicalMuscle, b: AnatomicalMuscle): number => {
    const byDeficit = deficitBy[b] - deficitBy[a];
    if (byDeficit !== 0) return byDeficit;
    if (lastTrainedAt) {
      const aAt = lastTrainedAt[a];
      const bAt = lastTrainedAt[b];
      if (!aAt && bAt) return -1;
      if (aAt && !bAt) return 1;
      if (aAt && bAt && aAt !== bAt) return Date.parse(aAt) - Date.parse(bAt);
    }
    const byGroup = DEFICIT_GROUP_PRIORITY.indexOf(MUSCLE_META[a].group)
      - DEFICIT_GROUP_PRIORITY.indexOf(MUSCLE_META[b].group);
    if (byGroup !== 0) return byGroup;
    return a < b ? -1 : a > b ? 1 : 0;
  };
  const deficits = ANATOMICAL_MUSCLES
    .filter((m) => deficitBy[m] > 0)
    .sort(worstFirst)
    .map((muscle) => ({ muscle, deficit: deficitBy[muscle] }));

  // D-DUE-1: the weekly count alone hides a muscle that met its target early in
  // the window and has rested since. Rested + last trained at least one target
  // spacing ago owes another session, ranked below true weekly gaps.
  const spacingHours = dueAgainSpacingHours(targetSessions);
  const asOfMs = asOfIso ? Date.parse(asOfIso) : Number.NaN;
  const needBy = {} as Record<AnatomicalMuscle, number>;
  const dueSet = new Set<AnatomicalMuscle>();
  for (const m of ANATOMICAL_MUSCLES) {
    needBy[m] = deficitBy[m];
    if (deficitBy[m] >= DUE_AGAIN_NEED || !Number.isFinite(asOfMs)) continue;
    if (!includeSpecialistMuscles && isSpecialistMuscle(m)) continue;
    const r = recovery?.[m];
    if (r?.state === "recovering") continue;
    const lastAt = lastTrainedAt?.[m] ?? r?.lastResistanceAt;
    if (!lastAt) continue;
    if ((asOfMs - Date.parse(lastAt)) / 3_600_000 >= spacingHours) {
      needBy[m] = DUE_AGAIN_NEED;
      dueSet.add(m);
    }
  }
  const dueMuscles = ANATOMICAL_MUSCLES.filter((m) => dueSet.has(m)).sort(worstFirst);

  const deferredMuscles: AnatomicalMuscle[] = recovery
    ? deficits
        .filter((d) => {
          const r = recovery[d.muscle];
          return r?.state === "recovering" && !isSameDayRecovering(r, timezone, asOfIso);
        })
        .map((d) => d.muscle)
    : [];
  const deferredSet = new Set(deferredMuscles);

  const blockedBucket = dominantBlockedBucket(recovery, timezone, asOfIso);

  interface PreparedCandidate {
    c: RecommendationCandidate;
    contrib: Map<AnatomicalMuscle, number>;
    gate: boolean;
    eligible: boolean;
    primarySet: Set<AnatomicalMuscle>;
  }

  const prepared: PreparedCandidate[] = [];
  for (const c of candidates) {
    if (!isHardPurpose(c)) continue;
    const contrib = new Map<AnatomicalMuscle, number>();
    // A muscle hit by both primary and secondary keeps the MAX weight, not the
    // sum — one exercise shouldn't double-count a single muscle.
    const add = (m: AnatomicalMuscle, w: number) =>
      contrib.set(m, Math.max(contrib.get(m) ?? 0, w));
    // D-DUE-2: specialist muscles count only as a primary mover, so a neck or
    // foot helper can never carry an exercise into Recommended on its own.
    const applyLevel = (
      ids: AnatomicalMuscle[] | undefined,
      groups: MuscleGroup[],
      weight: number,
      helper: boolean,
    ) => {
      const addLevel = (m: AnatomicalMuscle) => {
        if (helper && isSpecialistMuscle(m)) return;
        add(m, weight);
      };
      if (ids && ids.length > 0) {
        for (const m of ids) if (isAnatomicalMuscle(m)) addLevel(m);
        return;
      }
      for (const g of groups) {
        // cardio has no body region; full-body would flat-bump every candidate.
        if (g === "cardio" || g === "full-body") continue;
        for (const m of FALLBACK_MUSCLES_BY_GROUP[g] ?? []) addLevel(m);
      }
    };
    applyLevel(c.primaryMuscleIds, c.primaryMuscles, PRIMARY_WEIGHT, false);
    applyLevel(c.secondaryMuscleIds, c.secondaryMuscles, SECONDARY_WEIGHT, true);

    // Gate on undamped coverage (D-RR-9). Recovering primaries are a separate
    // eligibility drop; damping no longer exists to keep the section non-empty.
    let coverage = 0;
    for (const [m, w] of contrib) coverage += w * (needBy[m] ?? 0);
    const primarySet = new Set(primaryMusclesOf(c));
    const gate = coverage >= MIN_RECOMMENDATION_SCORE;
    prepared.push({
      c,
      contrib,
      gate,
      eligible: gate && isHardEligible(c, recovery, timezone, asOfIso),
      primarySet,
    });
  }

  const primarySameDay = (primarySet: Set<AnatomicalMuscle>): boolean => {
    for (const m of primarySet) {
      const r = recovery?.[m];
      if (r && isSameDayRecovering(r, timezone, asOfIso)) return true;
    }
    return false;
  };

  const needLeft = {} as Record<AnatomicalMuscle, number>;
  for (const m of ANATOMICAL_MUSCLES) needLeft[m] = Math.min(1, needBy[m]);

  const gainOf = (p: PreparedCandidate): number => {
    let gain = 0;
    for (const [m, w] of p.contrib) {
      const r = recovery?.[m];
      const sameDay = r ? isSameDayRecovering(r, timezone, asOfIso) : false;
      const factor = p.primarySet.has(m) ? 1 : recoveryFactor(r, sameDay);
      gain += Math.min(needLeft[m], w) * (needBy[m] ?? 0) * deficitScale * factor;
    }
    if (blockedBucket && ANTAGONIST[blockedBucket] === candidateBucket(p.c)) {
      gain *= ANTAGONIST_BOOST;
    }
    return gain;
  };

  const own: ScoredRecommendation[] = [];
  const pickedIds = new Set<string>();
  const remaining = prepared.filter((p) => p.c.source === "own" && p.eligible);
  while (own.length < MAX_BALANCE_RECOMMENDATIONS && remaining.length > 0) {
    let bestIdx = -1;
    let bestGain = Number.NEGATIVE_INFINITY;
    let bestName = "";
    for (let i = 0; i < remaining.length; i++) {
      const row = remaining[i]!;
      const g = gainOf(row);
      const name = row.c.name;
      if (bestIdx < 0 || g > bestGain || (g === bestGain && name < bestName)) {
        bestIdx = i;
        bestGain = g;
        bestName = name;
      }
    }
    if (bestIdx < 0 || bestGain <= 0) break;
    const pick = remaining[bestIdx]!;
    const coveredMuscles = [...pick.contrib.keys()]
      .filter((m) => needLeft[m] > 0)
      .sort(worstFirst);
    own.push({
      id: pick.c.id,
      source: "own",
      name: pick.c.name,
      score: bestGain,
      coveredMuscles,
      dueMuscles: coveredMuscles.filter((m) => dueSet.has(m)),
      restingMuscles: coveredMuscles.filter((m) => recovery?.[m]?.state === "recovering"),
      kind: "session",
      sameDay: primarySameDay(pick.primarySet),
      alternatives: [],
    });
    pickedIds.add(pick.c.id);
    for (const [m, w] of pick.contrib) needLeft[m] = Math.max(0, needLeft[m] - w);
    remaining.splice(bestIdx, 1);
  }

  const ownEligible = prepared.filter((p) => p.c.source === "own" && p.eligible);
  const preparedById = new Map(ownEligible.map((p) => [p.c.id, p]));
  for (const pick of own) {
    const picked = preparedById.get(pick.id);
    if (!picked) continue;
    const pp = primaryMusclesOf(picked.c);
    if (pp.length === 0) continue;
    const ppSet = new Set(pp);
    const alts = ownEligible
      .filter((p) => !pickedIds.has(p.c.id))
      .map((p) => {
        const ap = primaryMusclesOf(p.c);
        let shared = 0;
        for (const m of ap) if (ppSet.has(m)) shared++;
        return { id: p.c.id, name: p.c.name, overlap: shared / pp.length };
      })
      .filter((a) => a.overlap >= ALTERNATIVE_MIN_OVERLAP)
      .sort((a, b) => b.overlap - a.overlap || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
    pick.alternatives = alts.slice(0, MAX_ALTERNATIVES).map((a) => a.id);
  }

  const inventoryCovered = new Set<AnatomicalMuscle>();
  const catalogIdsOwned = new Set<string>();
  for (const p of prepared) {
    if (p.c.source !== "own") continue;
    for (const m of p.contrib.keys()) inventoryCovered.add(m);
    if (p.c.catalogId) catalogIdsOwned.add(p.c.catalogId);
  }
  const eligibleCatalog = prepared.filter(
    (p) => p.c.source === "catalog" && p.eligible && !catalogIdsOwned.has(p.c.id),
  );

  const rankSum = (p: PreparedCandidate, muscles: readonly AnatomicalMuscle[]): number => {
    let sum = 0;
    for (const m of muscles) sum += (p.contrib.get(m) ?? 0) * (deficitBy[m] ?? 0);
    return sum;
  };
  const toCatalog = (
    p: PreparedCandidate,
    score: number,
    catalogReason: "inventory-gap" | "variety",
  ): ScoredRecommendation => {
    const coveredMuscles = [...p.contrib.keys()].filter((m) => deficitBy[m] > 0).sort(worstFirst);
    return {
      id: p.c.id,
      source: "catalog",
      name: p.c.name,
      score,
      coveredMuscles,
      dueMuscles: [],
      restingMuscles: coveredMuscles.filter((m) => recovery?.[m]?.state === "recovering"),
      kind: "session",
      sameDay: primarySameDay(p.primarySet),
      alternatives: [],
      catalogReason,
    };
  };

  const gapMuscles = deficits
    .map((d) => d.muscle)
    .filter((m) => !inventoryCovered.has(m) && !deferredSet.has(m))
    .slice(0, CATALOG_FALLBACK_DEFICIT_MUSCLES);
  const catalog: ScoredRecommendation[] = [];
  const seenCatalog = new Set<string>();
  const gapRanked = eligibleCatalog
    .filter((p) => gapMuscles.some((m) => p.contrib.has(m)))
    .map((p) => ({ p, score: rankSum(p, gapMuscles) }))
    .sort((a, b) => b.score - a.score || byNameAsc(a.p.c, b.p.c));
  for (const row of gapRanked) {
    if (catalog.length >= MAX_CATALOG_FALLBACKS) break;
    if (seenCatalog.has(row.p.c.id)) continue;
    seenCatalog.add(row.p.c.id);
    catalog.push(toCatalog(row.p, row.score, "inventory-gap"));
  }

  if (catalog.length < MAX_CATALOG_FALLBACKS) {
    const varietyTargets = deficits
      .map((d) => d.muscle)
      .filter((m) => needLeft[m] > 0 && !deferredSet.has(m))
      .slice(0, CATALOG_FALLBACK_DEFICIT_MUSCLES);
    if (varietyTargets.length > 0) {
      const varietyRanked = eligibleCatalog
        .filter((p) => !seenCatalog.has(p.c.id) && varietyTargets.some((m) => p.contrib.has(m)))
        .map((p) => ({ p, score: rankSum(p, varietyTargets) }))
        .sort((a, b) => b.score - a.score || byNameAsc(a.p.c, b.p.c));
      const pool = varietyRanked.slice(0, VARIETY_POOL_SIZE);
      if (pool.length > 0) {
        const start = timezone && asOfIso
          ? hash32(localCalendarDate(asOfIso, timezone)) % pool.length
          : 0;
        const slots = MAX_CATALOG_FALLBACKS - catalog.length;
        for (let i = 0; i < slots && i < pool.length; i++) {
          const item = pool[(start + i) % pool.length]!;
          seenCatalog.add(item.p.c.id);
          catalog.push(toCatalog(item.p, item.score, "variety"));
        }
      }
    }
  }

  const reasonOrder: OwnNotShownReason[] = ["no-muscle-tags", "no-gap", "recovering", "covered-by-pick"];
  const buildOwnNotShown = (exclude: Set<string>): OwnNotShown[] => {
    const rows: OwnNotShown[] = [];
    for (const p of prepared) {
      if (p.c.source !== "own" || pickedIds.has(p.c.id) || exclude.has(p.c.id)) continue;
      const primaries = primaryMusclesOf(p.c);
      let reason: OwnNotShownReason;
      let readyInHours: number | undefined;
      if (primaries.length === 0) reason = "no-muscle-tags";
      else if (!p.gate) reason = "no-gap";
      else if (!p.eligible) {
        reason = "recovering";
        readyInHours = primaryRestHoursRemaining(primaries, recovery, timezone, asOfIso);
      } else reason = "covered-by-pick";
      rows.push({
        id: p.c.id,
        name: p.c.name,
        reason,
        ...(readyInHours !== undefined ? { readyInHours } : {}),
      });
    }
    rows.sort((a, b) => {
      const byReason = reasonOrder.indexOf(a.reason) - reasonOrder.indexOf(b.reason);
      if (byReason !== 0) return byReason;
      const byHours = (a.readyInHours ?? 0) - (b.readyInHours ?? 0);
      if (byHours !== 0) return byHours;
      return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
    });
    return rows;
  };

  // D-DUE-3: an empty strip is only a recovery day when something is actually
  // resting. Everything at target and ready is a plain empty Recommended.
  if (own.length + catalog.length > 0 || !recovery || !hasBlockedMuscle(recovery, timezone, asOfIso)) {
    return {
      deficits, dueMuscles, own, catalog, recoveryDay: false, deferredMuscles,
      ownNotShown: buildOwnNotShown(new Set()),
    };
  }

  const recoveryPurpose = candidates.filter(isRecoveryPurpose);
  const listA: ScoredRecommendation[] = [
    ...recoveryPurpose.filter((c) => c.source === "own").sort(byNameAsc),
    ...recoveryPurpose.filter((c) => c.source === "catalog").sort(byNameAsc),
  ].map((c) => ({
    id: c.id,
    source: c.source,
    name: c.name,
    score: 0,
    coveredMuscles: [] as AnatomicalMuscle[],
    dueMuscles: [] as AnatomicalMuscle[],
    restingMuscles: [] as AnatomicalMuscle[],
    kind: "recovery-mobility" as const,
    sameDay: false,
    alternatives: [],
  }));

  const listB: ScoredRecommendation[] = [];
  for (const c of candidates) {
    if (c.source !== "own" || !isHardPurpose(c) || isHardEligible(c, recovery, timezone, asOfIso)) continue;
    const primaries = primaryMusclesOf(c);
    if (primaries.length === 0) continue;
    const minFrac = Math.min(
      ...primaries.map((m) => recovery[m]?.fractionRecovered ?? 1),
    );
    if (minFrac < RECOVERY_LIGHT_MIN_FRACTION) continue;
    const coveredMuscles = primaries.filter((m) => deficitBy[m] > 0).sort(worstFirst);
    listB.push({
      id: c.id,
      source: c.source,
      name: c.name,
      score: minFrac,
      coveredMuscles,
      dueMuscles: [],
      restingMuscles: coveredMuscles.filter((m) => recovery[m]?.state === "recovering"),
      kind: "recovery-light",
      sameDay: false,
      alternatives: [],
    });
  }
  listB.sort((a, b) => b.score - a.score || byNameAsc(a, b));

  const capped = [...listA, ...listB].slice(0, MAX_RECOVERY_DAY_RECS);
  const shownLight = new Set(
    capped.filter((s) => s.kind === "recovery-light").map((s) => s.id),
  );
  return {
    deficits,
    dueMuscles,
    own: capped.filter((s) => s.source === "own"),
    catalog: capped.filter((s) => s.source === "catalog"),
    recoveryDay: true,
    deferredMuscles,
    ownNotShown: buildOwnNotShown(shownLight),
  };
}

/**
 * The anatomical muscles an exercise / catalog entry targets, for the detail
 * sheet's mini-map marker layer (TRAINER_LOG_DEDUPE_AND_REC_MUSCLE_EMPHASIS_PLAN
 * D-9). primary/secondary each: use the *MuscleIds array when it has entries,
 * else fan every coarse MuscleGroup out through FALLBACK_MUSCLES_BY_GROUP
 * (cardio / full-body contribute nothing — the same rule as
 * computeBalanceRecommendations' applyLevel). Each list is de-duplicated; a
 * muscle in both primary and secondary stays in primary only.
 */
export function exerciseTargetMuscleIds(src: {
  primaryMuscles?: MuscleGroup[];
  secondaryMuscles?: MuscleGroup[];
  primaryMuscleIds?: AnatomicalMuscle[];
  secondaryMuscleIds?: AnatomicalMuscle[];
}): { primary: AnatomicalMuscle[]; secondary: AnatomicalMuscle[] } {
  const resolve = (
    ids: AnatomicalMuscle[] | undefined,
    groups: MuscleGroup[] | undefined,
  ): AnatomicalMuscle[] => {
    const out: AnatomicalMuscle[] = [];
    const push = (m: AnatomicalMuscle) => {
      if (!out.includes(m)) out.push(m);
    };
    if (ids && ids.length > 0) {
      for (const m of ids) if (isAnatomicalMuscle(m)) push(m);
      return out;
    }
    for (const g of groups ?? []) {
      if (g === "cardio" || g === "full-body") continue;
      for (const m of FALLBACK_MUSCLES_BY_GROUP[g] ?? []) push(m);
    }
    return out;
  };

  const primary = resolve(src.primaryMuscleIds, src.primaryMuscles);
  const primarySet = new Set(primary);
  const secondary = resolve(src.secondaryMuscleIds, src.secondaryMuscles).filter(
    (m) => !primarySet.has(m),
  );
  return { primary, secondary };
}

export interface AntagonistPair {
  id: string;
  /** e.g. "Push vs Pull". */
  label: string;
  aLabel: string;
  bLabel: string;
  a: AnatomicalMuscle[];
  b: AnatomicalMuscle[];
  /** Typical [min, max] for load(a)/load(b). Outside -> a distribution note. */
  typicalBand: [number, number];
}

const UPPER_BODY_GROUPS: MuscleGroup[] = ["chest", "back", "shoulders", "biceps", "triceps", "forearms"];
const LOWER_BODY_GROUPS: MuscleGroup[] = ["glutes", "quads", "hamstrings", "calves", "hip-abductors", "adductors", "hip-flexors"];
const musclesInGroups = (groups: MuscleGroup[]): AnatomicalMuscle[] =>
  ANATOMICAL_MUSCLES.filter((m) => groups.includes(MUSCLE_META[m].group));

// Each pair's ratio is partly an artefact of how many anatomical ids sit on
// each side (e.g. quads 3 vs hamstrings 2). Rebalancing that is a Stage 4 task
// (TRAINER_ACCURACY audit F12); these bands are training-distribution
// comparisons, not "healthy" strength or torque ranges.
export const ANTAGONIST_PAIRS: AntagonistPair[] = [
  { id: "push-pull", label: "Push vs Pull", aLabel: "Push", bLabel: "Pull",
    a: ["pec-clavicular", "pec-sternal", "delt-anterior", "triceps-long", "triceps-lateral"],
    b: ["lat-dorsi", "traps-mid", "rhomboids", "delt-posterior", "biceps-brachii", "brachialis"],
    typicalBand: [0.7, 1.4] },
  { id: "quad-ham", label: "Quads vs Hamstrings", aLabel: "Quads", bLabel: "Hamstrings",
    a: ["rectus-femoris", "vastus-lateralis", "vastus-medialis"],
    b: ["biceps-femoris", "semitendinosus"],
    typicalBand: [1.0, 2.0] },
  { id: "delt-front-back", label: "Front vs Rear Delts", aLabel: "Front delts", bLabel: "Rear delts",
    a: ["delt-anterior"], b: ["delt-posterior"], typicalBand: [0.6, 1.6] },
  { id: "abs-lowback", label: "Abs vs Lower Back", aLabel: "Abs", bLabel: "Lower back",
    a: ["rectus-abdominis", "obliques"], b: ["erector-spinae"], typicalBand: [0.6, 2.0] },
  { id: "upper-lower", label: "Upper vs Lower Body", aLabel: "Upper", bLabel: "Lower",
    a: musclesInGroups(UPPER_BODY_GROUPS),
    b: musclesInGroups(LOWER_BODY_GROUPS),
    typicalBand: [0.6, 1.8] },
  { id: "abd-add", label: "Abductors vs Adductors", aLabel: "Abductors", bLabel: "Adductors",
    a: ["tfl", "glute-medius", "glute-minimus"], b: ["hip-adductors", "gracilis"],
    typicalBand: [0.6, 1.6] },
  { id: "hipflex-glute", label: "Hip Flexors vs Glutes", aLabel: "Hip flexors", bLabel: "Glutes",
    a: ["psoas-major", "iliacus", "sartorius"], b: ["glute-maximus", "glute-medius", "glute-minimus"],
    typicalBand: [0.4, 1.2] },
  { id: "neck-flex-ext", label: "Neck Flexors vs Extensors", aLabel: "Neck flexors", bLabel: "Neck extensors",
    a: ["deep-cervical-flexors", "sternocleidomastoid", "scalenes"],
    b: ["traps-upper", "splenius-capitis", "levator-scapulae"],
    typicalBand: [0.5, 1.5] },
];

export interface AntagonistRatio {
  pair: AntagonistPair;
  aLoad: number;
  bLoad: number;
  /** aLoad / bLoad, or null when status is "insufficient-data". */
  ratio: number | null;
  status: "balanced" | "a-dominant" | "b-dominant" | "insufficient-data";
  /** Neutral, informational phrasing — never a prescription (matches D-GYM-4 posture). */
  message: string;
}

/** Mirrors computeBalanceCallouts's guard style: below this combined load the
 *  pair reports "insufficient-data" rather than a misleading ratio. */
const RATIO_MIN_COMBINED_LOAD = 3.0;

function sumLoad(load: AnatomicalLoad, muscles: AnatomicalMuscle[]): number {
  return muscles.reduce((sum, m) => sum + (load.muscle[m] ?? 0), 0);
}

/**
 * Computes antagonist-pair balance ratios (D-BM-9) from an AnatomicalLoad.
 * Rules: aLoad + bLoad < RATIO_MIN_COMBINED_LOAD -> insufficient-data.
 * bLoad === 0 && aLoad > 0 -> a-dominant, ratio: null. Otherwise ratio below
 * band -> b-dominant, above -> a-dominant, inside -> balanced.
 */
export function computeAntagonistRatios(load: AnatomicalLoad): AntagonistRatio[] {
  return ANTAGONIST_PAIRS.map((pair) => {
    const aLoad = sumLoad(load, pair.a);
    const bLoad = sumLoad(load, pair.b);

    if (aLoad + bLoad < RATIO_MIN_COMBINED_LOAD) {
      return {
        pair, aLoad, bLoad, ratio: null, status: "insufficient-data",
        message: `Not enough logged ${pair.label.toLowerCase()} work in this window to compare.`,
      };
    }

    if (bLoad === 0 && aLoad > 0) {
      return {
        pair, aLoad, bLoad, ratio: null, status: "a-dominant",
        message: `Your logs in this window include ${pair.aLabel.toLowerCase()} work and no ${pair.bLabel.toLowerCase()} work.`,
      };
    }

    const ratio = bLoad > 0 ? aLoad / bLoad : 0;
    const [min, max] = pair.typicalBand;
    if (ratio < min) {
      return {
        pair, aLoad, bLoad, ratio, status: "b-dominant",
        message: `Your logged ${pair.bLabel.toLowerCase()} work outweighs your ${pair.aLabel.toLowerCase()} work in this window.`,
      };
    }
    if (ratio > max) {
      return {
        pair, aLoad, bLoad, ratio, status: "a-dominant",
        message: `Your logged ${pair.aLabel.toLowerCase()} work outweighs your ${pair.bLabel.toLowerCase()} work in this window.`,
      };
    }
    return {
      pair, aLoad, bLoad, ratio, status: "balanced",
      message: `Your logged ${pair.aLabel.toLowerCase()} and ${pair.bLabel.toLowerCase()} work are in similar proportion in this window.`,
    };
  });
}

// ── Left-vs-Right balance ─────────────────────────────────────────────────
// Modelled on computeAntagonistRatios (same insufficient-data guard, same
// neutral non-prescriptive phrasing) but over a LateralLoad instead of an
// AnatomicalLoad. Additive: it reads nothing the old ratio math reads and feeds
// nothing it feeds.

/** Healthy [min, max] for leftLoad / rightLoad — tighter than the antagonist
 *  bands, since left and right should be near-symmetric (D-LAT-6). */
export const LATERAL_HEALTHY_BAND: [number, number] = [0.8, 1.25];

export interface LateralBalanceRow {
  /** "overall", or a coarse MuscleGroup. */
  key: string;
  label: string;
  leftLoad: number;
  rightLoad: number;
  /** leftLoad / rightLoad, or null when status is "insufficient-data" or one side is untrained. */
  ratio: number | null;
  status: "balanced" | "left-dominant" | "right-dominant" | "insufficient-data";
  /** Neutral, informational phrasing — never a prescription (matches D-GYM-4 posture). */
  message: string;
}

/** Title-cases a hyphenated MuscleGroup id, e.g. "hip-abductors" -> "Hip Abductors". */
function groupLabel(g: MuscleGroup): string {
  return g.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function sumSide(rec: Record<AnatomicalMuscle, number>, muscles: AnatomicalMuscle[]): number {
  return muscles.reduce((sum, m) => sum + (rec[m] ?? 0), 0);
}

function lateralRow(
  key: string,
  label: string,
  muscles: AnatomicalMuscle[],
  load: LateralLoad,
): LateralBalanceRow | null {
  const leftLoad = sumSide(load.left, muscles);
  const rightLoad = sumSide(load.right, muscles);

  // Rows with no load at all are omitted entirely (the caller drops nulls).
  if (leftLoad + rightLoad === 0) return null;

  if (leftLoad + rightLoad < RATIO_MIN_COMBINED_LOAD) {
    return {
      key, label, leftLoad, rightLoad, ratio: null, status: "insufficient-data",
      message: `Not enough ${label.toLowerCase()} data yet in this window.`,
    };
  }

  if (rightLoad === 0 && leftLoad > 0) {
    return {
      key, label, leftLoad, rightLoad, ratio: null, status: "left-dominant",
      message: `Left side has been trained with no matching right-side work in this window.`,
    };
  }
  if (leftLoad === 0 && rightLoad > 0) {
    return {
      key, label, leftLoad, rightLoad, ratio: null, status: "right-dominant",
      message: `Right side has been trained with no matching left-side work in this window.`,
    };
  }

  const ratio = leftLoad / rightLoad;
  const [min, max] = LATERAL_HEALTHY_BAND;
  if (ratio < min) {
    return {
      key, label, leftLoad, rightLoad, ratio, status: "right-dominant",
      message: `Right load is higher than left in this window.`,
    };
  }
  if (ratio > max) {
    return {
      key, label, leftLoad, rightLoad, ratio, status: "left-dominant",
      message: `Left load is higher than right in this window.`,
    };
  }
  return {
    key, label, leftLoad, rightLoad, ratio, status: "balanced",
    message: `Left and right are roughly balanced in this window.`,
  };
}

/**
 * One "overall" row (summed over every paired muscle) followed by one row per
 * coarse MuscleGroup — in MUSCLE_GROUPS order, excluding `cardio` and
 * `full-body`, and restricted to that group's PAIRED muscles. Rows whose
 * combined left+right load is 0 are omitted. When LateralLoad.sidedSessionCount
 * is 0 every row is trivially "balanced"; the UI must show D-LAT-5's empty state
 * rather than render those rows.
 */
export function computeLateralBalance(load: LateralLoad): LateralBalanceRow[] {
  const rows: LateralBalanceRow[] = [];
  const allPaired = ANATOMICAL_MUSCLES.filter((m) => MUSCLE_META[m].paired);
  const overall = lateralRow("overall", "Overall", allPaired, load);
  if (overall) rows.push(overall);

  // MUSCLES_BY_GROUP is built in this file from a literal whose key order matches
  // MUSCLE_GROUPS exactly — iterating its keys avoids a runtime value import of
  // MUSCLE_GROUPS from gym.ts (which imports this module) and the cycle that adds.
  for (const g of Object.keys(MUSCLES_BY_GROUP) as MuscleGroup[]) {
    if (g === "cardio" || g === "full-body") continue;
    const paired = MUSCLES_BY_GROUP[g].filter((m) => MUSCLE_META[m].paired);
    if (paired.length === 0) continue;
    const row = lateralRow(g, groupLabel(g), paired, load);
    if (row) rows.push(row);
  }
  return rows;
}
